import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import {
  CropObject,
  EnhancedFileItem,
  ErrorFile,
  FilesListT,
  PhotoType,
} from "shared/types/media";

import { getAvatarThumbnailSrc } from "shared/components/Avatar";
import WebCameraModal from "shared/components/Modals/WebCameraModal";
import WebCamErrorModal from "shared/components/Modals/WebCameraModal/WebCamErrorModal";
import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_STATIC,
} from "shared/components/SpinLoader";
import FilesUploader from "shared/components/UIComponents/FilesUploadComponents/FilesUploader";

import { deleteProfileFile, uploadProfileFiles } from "shared/data/media";

import {
  getSizeInMb,
  PROFILE_UPLOAD_MAX_PHOTOS,
  UPLOAD_IMAGE_EXTENSION,
  UPLOAD_IMAGE_FILE_FORMATS,
  UPLOAD_MAX_IMAGE_FILE_SIZE,
} from "shared/constants/files";
import { LOCAL_STORAGE_KEYS } from "shared/constants/storageKeys";

import { simplyClick } from "shared/helpers/analytics";

import { useLocalStorage } from "shared/hooks/useLocalStorage";

import { CropModal } from "./CropModal";
import { ErrorModal } from "./ErrorModal";

import classes from "@platformAssets/components/UploadMediaNew.module.scss";

import { ReactComponent as TrashIcon } from "assets/shared/img/ico_trash_color.svg";
import { ReactComponent as PlaceholderIcon } from "assets/shared/img/photograph.svg";

const STEP_INIT = 1;
const STEP_SHOW_CROP_MODAL = 2;
const STEP_SHOW_WEBCAM_MODAL = 3;
const STEP_SHOW_WEBCAM_MODAL_ERROR = 4;
const STEP_LOAD_PHOTO = 5;
const STEP_ADDED_PHOTO = 6;

type Props = {
  analytics?: Record<string, string[]>;
  onLoad?: () => void;
  onLoaded?: () => void;
  setIsUploaded?: (value: boolean) => void;
  title?: string;
};

const UploadMedia: React.FC<Props> = ({
  analytics,
  setIsUploaded,
  title,
  onLoad,
  onLoaded,
}): JSX.Element => {
  const { t } = useTranslation("uploadMedia");
  const inputFileRef = useRef<
    HTMLInputElement & { handleClickUpload: () => void }
  >(null);
  const [file, setFile] = useState<EnhancedFileItem | null>(null);
  const [uploadedPhoto, setUploadedPhoto, deleteUploadedPhoto] =
    useLocalStorage<PhotoType | null>(LOCAL_STORAGE_KEYS.USER_AVATAR, null);
  const [step, setStep] = useState<number>(STEP_INIT);
  const [fileError, setFileError] = useState<ErrorFile>({
    show: false,
    text: null,
  });
  const [fileLoaded, setFileLoaded] = useState(false);
  const [cropFile, setCropFile] = useState<EnhancedFileItem | null>(null);

  useEffect(() => {
    if (file && step < STEP_LOAD_PHOTO && !fileError.show) {
      setStep(STEP_SHOW_CROP_MODAL);
      setCropFile(file);
    }

    if (file && step === STEP_LOAD_PHOTO) {
      if (typeof onLoad === "function") {
        onLoad();
      }

      uploadProfileFiles(
        file,
        true,
        (data: PhotoType) => {
          if (typeof onLoaded === "function") {
            onLoaded();
          }

          setUploadedPhoto(data);
          setStep(STEP_ADDED_PHOTO);
          if (typeof setIsUploaded === "function") setIsUploaded(true);
        },
        () => {
          setFileError({ show: true, text: "" });
        }
      );
    }
  }, [file, step, fileError]);

  useEffect(() => {
    if (uploadedPhoto) {
      setStep(STEP_ADDED_PHOTO);
    }

    if (uploadedPhoto && typeof setIsUploaded === "function") {
      setIsUploaded(true);
    }
  }, [uploadedPhoto]);

  const addFileItems = (filesList: FilesListT, isWebCam = false): void => {
    setFile(
      filesList.files.map((file) => {
        const src =
          typeof URL !== "undefined" && URL.createObjectURL
            ? URL.createObjectURL(file.file)
            : null;

        return {
          ...file,
          ...{
            preview: src,
            isWebCam,
            cropped: false,
            crop: {},
            src,
          },
        };
      })[0]
    );
  };

  const handleFileError = (error: ErrorFile): void => {
    analytics?.add?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    setFileError(error);
  };

  const clickSelectFiles = (): void => {
    analytics?.add?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    inputFileRef.current?.handleClickUpload();
  };

  const clickSelectWebcam = (): void => {
    analytics?.take?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    setStep(STEP_SHOW_WEBCAM_MODAL);
  };

  const onClickDeletePhoto = (): void => {
    if (uploadedPhoto) {
      analytics?.addedDelete?.forEach((item: string) => {
        simplyClick(item, null, false);
      });

      deleteProfileFile(
        uploadedPhoto.uid,
        true,
        () => {
          setStep(STEP_INIT);
          setFileLoaded(false);
          setFile(null);
          deleteUploadedPhoto();
          if (typeof setIsUploaded === "function") setIsUploaded(false);
        },
        () => {
          console.log("Delete error");
        }
      );
    }
  };

  const changeCropPreview = (previewString: string): void => {
    setCropFile({ ...cropFile, preview: previewString } as EnhancedFileItem);
  };

  const handleSuccessCrop = (data: CropObject): void => {
    setFile({ ...file, crop: data, cropped: true } as EnhancedFileItem);
    setStep(STEP_LOAD_PHOTO);
  };

  const handleChangeCropData = (data: CropObject): void => {
    setCropFile({ ...cropFile, crop: data, cropped: true } as EnhancedFileItem);
  };

  const closeCrop = (): void => {
    setStep(STEP_INIT);
    setFileLoaded(false);
    setFile(null);

    analytics?.cropClose?.forEach((item: string) => {
      simplyClick(item, null, false);
    });
  };

  const closeErrorModal = (): void => {
    setFileError({ show: false, text: null });
    setStep(STEP_INIT);
    setFileLoaded(false);
    setFile(null);

    analytics?.loadingErrorClose?.forEach((item: string) => {
      simplyClick(item, null, false);
    });
  };
  const continueErrorModal = (): void => {
    setFileError({ show: false, text: null });
    setStep(STEP_INIT);
    setFileLoaded(false);
    setFile(null);

    analytics?.loadingErrorContinue?.forEach((item: string) => {
      simplyClick(item, null, false);
    });
  };

  return (
    <>
      <div className={classes.title}>{title || t("title")}</div>
      <div className={classes.description}>
        {t("common:maxFileSizeText", {
          size: getSizeInMb(UPLOAD_MAX_IMAGE_FILE_SIZE),
        })}
      </div>
      <div className={classes.description}>{t("acceptableFormats")}</div>
      <div
        className={cn(classes.photo, {
          [classes.imagePhoto]: step === STEP_ADDED_PHOTO,
        })}
      >
        {step === STEP_INIT && <PlaceholderIcon />}

        {((step === STEP_SHOW_CROP_MODAL && !fileLoaded) ||
          step === STEP_LOAD_PHOTO) && (
          <SpinLoader
            size={LOADER_SIZE_LARGE}
            color={LOADER_COLOR_DARK}
            position={POSITION_STATIC}
          />
        )}

        {step === STEP_ADDED_PHOTO && (
          <>
            <img
              src={getAvatarThumbnailSrc(uploadedPhoto) || ""}
              className={classes.imagePhoto}
            />
            <div className={classes.trash} onClick={onClickDeletePhoto}>
              <TrashIcon />
            </div>
          </>
        )}
      </div>
      {step === STEP_INIT && (
        <>
          <button
            className={cn(classes.button, classes.firstButton)}
            onClick={clickSelectFiles}
          >
            {t("uploadDevice")}
          </button>
          <div className={classes.buttonLinkContainer}>
            <button
              className={cn(classes.button, classes.secondButton)}
              onClick={clickSelectWebcam}
            >
              {t("uploadWebcam")}
            </button>
          </div>
        </>
      )}
      <div className={classes.input}>
        <FilesUploader
          ref={inputFileRef}
          key="user-file-select"
          addFileItems={(data: FilesListT) => addFileItems(data, false)}
          fileItems={{ "user-media-files": { files: file ? [file] : [] } }}
          showFileError={handleFileError}
          identity="user-media-files"
          isFileUploadModal
          acceptFiles={UPLOAD_IMAGE_FILE_FORMATS}
          acceptExtension={UPLOAD_IMAGE_EXTENSION}
          multiple={false}
          maxFiles={PROFILE_UPLOAD_MAX_PHOTOS}
        />
      </div>
      {step === STEP_SHOW_CROP_MODAL && (
        <div
          className={cn(classes.cropContainer, {
            active: fileLoaded,
          })}
        >
          <CropModal
            analytics={analytics}
            close={closeCrop}
            cropSrc={cropFile?.preview as string}
            initImg={cropFile?.src as string}
            key="user-file-crop"
            setCropData={handleChangeCropData}
            setCropSrc={changeCropPreview}
            setFileLoaded={setFileLoaded}
            successAction={handleSuccessCrop}
          />
        </div>
      )}
      {step === STEP_SHOW_WEBCAM_MODAL && (
        <WebCameraModal
          close={() => setStep(STEP_INIT)}
          key="file-webcam"
          addFileItems={(data: FilesListT) => addFileItems(data, true)}
          showFileError={handleFileError}
          handleError={() => setStep(STEP_SHOW_WEBCAM_MODAL_ERROR)}
        />
      )}
      {fileError.show && (
        <ErrorModal
          close={closeErrorModal}
          handleContinue={continueErrorModal}
        />
      )}
      {step === STEP_SHOW_WEBCAM_MODAL_ERROR && (
        <WebCamErrorModal
          key={"file-webcam-error"}
          close={() => setStep(STEP_INIT)}
          tryAgain={() => setStep(STEP_SHOW_WEBCAM_MODAL)}
        />
      )}
    </>
  );
};

export default UploadMedia;
